import React, { useEffect, useState, useContext } from 'react';
import { Table, Button, Modal, Form, Select, Tag, message, Upload, List, DatePicker, Steps, Divider, Card, Progress } from 'antd';
import { PlusOutlined, UploadOutlined, FileTextOutlined, EditOutlined, DownloadOutlined, UserOutlined, AuditOutlined, BankOutlined, SafetyCertificateOutlined, FileDoneOutlined, PlayCircleOutlined, CheckCircleOutlined, StopOutlined, SolutionOutlined, CalendarOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { GetAssignacions, PostAssignacio, PutAssignacioEstat } from '../../../dao/metadata/ManageAssignacions';
import { GetEmpresas } from '../../../dao/metadata/GetEmpresas';
import { GetAlumnesMetadata } from '../../../dao/metadata/GetAlumnesMetadata';
import { GetDocuments, UploadDocument } from '../../../dao/metadata/ManageDocuments';
import { AuthContext } from '../../../config/AuthContext';
import dayjs from 'dayjs';

const { Option } = Select;

const ESTADOS = [
    { id: 0, label: 'Entrevista', color: 'blue', icon: <UserOutlined /> },
    { id: -2, label: 'No seleccionado', color: 'default', icon: <CloseCircleOutlined /> },
    { id: 1, label: 'Pendiente Acuerdo', color: 'cyan', icon: <AuditOutlined /> },
    { id: 4, label: 'Firma Centro', color: 'purple', icon: <SafetyCertificateOutlined /> },
    { id: 3, label: 'Firma Empresa', color: 'geekblue', icon: <BankOutlined /> },
    { id: 2, label: 'Firma Alumno', color: 'orange', icon: <EditOutlined /> },
    { id: 5, label: 'Entrega Resp.', color: 'magenta', icon: <FileDoneOutlined /> },
    { id: 6, label: 'En curso', color: 'green', icon: <PlayCircleOutlined /> },
    { id: 7, label: 'Finalizada', color: 'gold', icon: <CheckCircleOutlined /> },
    { id: 8, label: 'Pendiente de Junta', color: 'volcano', icon: <SolutionOutlined /> },
    { id: -1, label: 'Cancelada', color: 'red', icon: <StopOutlined /> },
];

const Asignaciones = () => {
    const { userId } = useContext(AuthContext);
    const [assignacions, setAssignacions] = useState<any[]>([]);
    const [empresas, setEmpresas] = useState<any[]>([]);
    const [alumnes, setAlumnes] = useState<any[]>([]);

    const [loading, setLoading] = useState(false);

    // Create Modal
    const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
    const [createForm] = Form.useForm();

    // Edit/Docs Modal
    const [isDetailModalVisible, setIsDetailModalVisible] = useState(false);
    const [selectedAssignacio, setSelectedAssignacio] = useState<any>(null);
    const [documents, setDocuments] = useState<any[]>([]);

    // Dates Modal
    const [isDatesModalVisible, setIsDatesModalVisible] = useState(false);
    const [datesForm] = Form.useForm();

    useEffect(() => {
        fetchData();
    }, []);

    const calculateProgress = (start?: string, end?: string) => {
        if (!start || !end) return 0;
        const s = dayjs(start).startOf('day');
        const e = dayjs(end).startOf('day');
        const now = dayjs().startOf('day');
        
        if (now.isBefore(s)) return 0;
        if (now.isAfter(e) || now.isSame(e)) return 100;
        
        const total = e.diff(s, 'day');
        const current = now.diff(s, 'day');
        
        if (total <= 0) return 100;
        return Math.min(100, Math.max(0, Math.round((current / total) * 100)));
    };

    const fetchData = async () => {
        setLoading(true);
        const [assData, empData, aluData] = await Promise.all([
            GetAssignacions(),
            GetEmpresas(),
            GetAlumnesMetadata("0") // Fetch all students
        ]);
        setAssignacions(assData || []);
        setEmpresas(empData || []);
        setAlumnes(aluData || []);
        setLoading(false);
    };

    const handleCreate = async () => {
        try {
            const values = await createForm.validateFields();
            await PostAssignacio({ ...values, estat: 0 }); // Default start state
            message.success('Asignación creada');
            setIsCreateModalVisible(false);
            createForm.resetFields();
            fetchData();
        } catch (error) {
            console.error(error);
        }
    };

    const handleStatusChange = async (newStatus: number, record?: any) => {
        const target = record || selectedAssignacio;
        if (!target) return;

        // If "En curso" (6), require dates
        if (newStatus === 6) {
            setSelectedAssignacio(target);
            // Check if existing dates, fill form
            if (target.data_inici_curs) {
                datesForm.setFieldsValue({
                    data_inici: dayjs(target.data_inici_curs),
                    data_fi: dayjs(target.data_fi_curs)
                });
            } else {
                datesForm.resetFields();
            }
            setIsDatesModalVisible(true);
            return;
        }

        await updateStatus(newStatus, undefined, target);
    };

    const updateStatus = async (newStatus: number, dates?: { data_inici: any, data_fi: any }, record?: any) => {
        const target = record || selectedAssignacio;
        await PutAssignacioEstat({
            id: target.id,
            estat_nou: newStatus,
            observacions: "Cambio de estado manual",
            usuari_canvi: parseInt(userId || "0"),
            data_inici: dates?.data_inici,
            data_fi: dates?.data_fi
        });
        message.success('Estado actualizado');
        // Update local state
        const updated = { ...selectedAssignacio, estat: newStatus };
        if (dates) {
            updated.data_inici_curs = dates.data_inici;
            updated.data_fi_curs = dates.data_fi;
        }
        setSelectedAssignacio(updated);
        fetchData();
    };

    const handleDatesSubmit = async () => {
        try {
            const values = await datesForm.validateFields();
            setIsDatesModalVisible(false);
            await updateStatus(6, {
                data_inici: values.data_inici.format('YYYY-MM-DD'),
                data_fi: values.data_fi.format('YYYY-MM-DD')
            });
        } catch (error) {
            // Validation failed
        }
    };

    const handleDownloadDoc = async (docId: number, docNom: string) => {
        try {
            const baseUrl = (process.env.REACT_APP_API_URL || 'http://localhost').replace(/\/$/, "");
            const res = await fetch(`${baseUrl}:8061/metadata/documents/download/${docId}`, {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` }
            });

            if (!res.ok) {
                message.error('Error al descarregar el document.');
                return;
            }

            const blob = await res.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.style.display = 'none';
            a.href = url;
            a.download = docNom;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (e) {
            message.error('Error descarregant document');
            console.error(e);
        }
    };

    const openDetail = async (record: any) => {
        setSelectedAssignacio(record);
        setIsDetailModalVisible(true);
        // Fetch docs
        const docs = await GetDocuments(record.id);
        setDocuments(docs || []);
    };

    const handleUpload = async (options: any) => {
        const { file, onSuccess, onError } = options;
        const res = await UploadDocument(selectedAssignacio.id, file);
        if (res) {
            message.success('Documento subido');
            const docs = await GetDocuments(selectedAssignacio.id);
            setDocuments(docs || []);
            onSuccess("Ok");
        } else {
            message.error('Error al subir');
            onError("Error");
        }
    };

    const getStatusLabel = (id: number) => ESTADOS.find(e => e.id === id)?.label || 'Desconocido';
    const getStatusColor = (id: number) => ESTADOS.find(e => e.id === id)?.color || 'default';

    const DocumentCell = ({ assignId }: { assignId: number }) => {
        const [docs, setDocs] = useState<any[]>([]);
        useEffect(() => {
            GetDocuments(assignId).then(d => setDocs(d || []));
        }, [assignId]);

        if (!docs.length) return <span style={{ color: '#ccc', fontSize: '12px' }}>Sense docs</span>;

        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {docs.map(doc => (
                    <Button 
                        key={doc.id}
                        size="small" 
                        type="dashed" 
                        icon={<DownloadOutlined />} 
                        onClick={() => handleDownloadDoc(doc.id, doc.nom)}
                        style={{ fontSize: '11px', textAlign: 'left' }}
                    >
                        {doc.nom.length > 15 ? doc.nom.substring(0, 15) + '...' : doc.nom}
                    </Button>
                ))}
            </div>
        );
    };

    const columns = [
        {
            title: 'Alumno',
            key: 'alumno',
            render: (_: any, r: any) => <span>{r.alumne_nom} {r.alumne_cognoms}</span>
        },
        {
            title: 'Empresa',
            key: 'empresa',
            render: (_: any, r: any) => (
                <div>
                    <div><b>{r.empresa_nom}</b></div>
                    {[6, 7, 8].includes(r.estat) && <div style={{ fontSize: '11px', color: '#888' }}>CIF: {r.empresa_cif}</div>}
                </div>
            )
        },
        {
            title: 'Periodo',
            key: 'periodo',
            render: (_: any, r: any) => {
                const showDates = [6, 7, 8].includes(r.estat);
                if (!showDates || !r.data_inici_curs) return null;
                return (
                    <div style={{ fontSize: '12px' }}>
                        <div><b>Ini:</b> {dayjs(r.data_inici_curs).format('DD/MM/YYYY')}</div>
                        <div><b>Fin:</b> {dayjs(r.data_fi_curs).format('DD/MM/YYYY')}</div>
                    </div>
                );
            }
        },
        {
            title: 'Progreso',
            key: 'progreso',
            width: 150,
            render: (_: any, r: any) => {
                const prog = calculateProgress(r.data_inici_curs, r.data_fi_curs);
                return <Progress percent={prog} size="small" status={prog === 100 ? 'success' : 'active'} />;
            }
        },
        {
            title: 'Estado',
            dataIndex: 'estat',
            key: 'estat',
            render: (estat: number, record: any) => {
                const prog = calculateProgress(record.data_inici_curs, record.data_fi_curs);
                return (
                    <Select
                        value={estat}
                        style={{ width: '100%' }}
                        onChange={(val) => handleStatusChange(val, record)}
                        bordered={false}
                        dropdownStyle={{ minWidth: 200 }}
                    >
                        {ESTADOS.map(e => (
                            <Option 
                                key={e.id} 
                                value={e.id}
                                disabled={e.id === 8 && prog < 100}
                            >
                                <Tag color={e.color} icon={e.icon} style={{ margin: 0 }}>{e.label}</Tag>
                            </Option>
                        ))}
                    </Select>
                );
            }
        },
        {
            title: 'Docs',
            key: 'docs',
            width: 160,
            render: (_: any, r: any) => <DocumentCell assignId={r.id} />
        },
        {
            title: 'Acciones',
            key: 'acciones',
            render: (_: any, r: any) => (
                <Button icon={<EditOutlined />} onClick={() => openDetail(r)}>Detalle</Button>
            )
        }
    ];

    return (
        <div>
            <div style={{ marginBottom: 16 }}>
                <Button type="primary" icon={<PlusOutlined />} onClick={() => setIsCreateModalVisible(true)}>
                    Nueva Asignación
                </Button>
            </div>

            <Table
                columns={columns}
                dataSource={assignacions}
                rowKey="id"
                loading={loading}
            />

            {/* CREATE MODAL */}
            <Modal
                title="Nueva Asignación"
                open={isCreateModalVisible}
                onOk={handleCreate}
                onCancel={() => setIsCreateModalVisible(false)}
            >
                <Form form={createForm} layout="vertical">
                    <Form.Item name="id_alumne" label="Alumno" rules={[{ required: true }]}>
                        <Select showSearch optionFilterProp="children">
                            {alumnes.map(a => (
                                <Option key={a.id} value={a.id}>{a.nom} {a.cognoms}</Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Form.Item name="id_empresa" label="Empresa" rules={[{ required: true }]}>
                        <Select showSearch optionFilterProp="children">
                            {empresas.map(e => (
                                <Option key={e.id} value={e.id}>{e.nom}</Option>
                            ))}
                        </Select>
                    </Form.Item>
                </Form>
            </Modal>

            {/* DETAIL MODAL */}
            <Modal
                title="Detalle Asignación"
                open={isDetailModalVisible}
                onCancel={() => setIsDetailModalVisible(false)}
                footer={null}
                width={800}
            >
                {selectedAssignacio && (
                    <div>
                    <Card size="small" style={{ marginBottom: 24, background: '#f5f5f5', border: 'none' }}>
                        <h3 style={{ marginBottom: 20 }}>Progreso de la Asignación</h3>
                        <Steps
                            direction="vertical"
                            size="small"
                            current={ESTADOS.findIndex(e => e.id === selectedAssignacio.estat)}
                            onChange={(current) => {
                                const states = ESTADOS.filter(e => e.id >= 0);
                                const newStatus = states[current].id;
                                const prog = calculateProgress(selectedAssignacio.data_inici_curs, selectedAssignacio.data_fi_curs);
                                
                                if (newStatus === 8 && prog < 100) {
                                    message.warning('Solo se puede pasar a Pendiente de Junta cuando ha finalizado el período de prácticas.');
                                    return;
                                }
                                handleStatusChange(newStatus);
                            }}
                            items={ESTADOS.filter(e => e.id >= 0).map((e) => ({
                                title: e.label,
                                icon: e.icon,
                                disabled: e.id === 8 && calculateProgress(selectedAssignacio.data_inici_curs, selectedAssignacio.data_fi_curs) < 100
                            }))}
                        />
                        {selectedAssignacio.estat === 6 && (
                            <>
                                <Divider />
                                <div style={{ textAlign: 'center' }}>
                                    <div style={{ marginBottom: 8, fontSize: '13px', color: '#666' }}>
                                        <b>Periodo actual:</b> {dayjs(selectedAssignacio.data_inici_curs).format('DD/MM/YYYY')} - {dayjs(selectedAssignacio.data_fi_curs).format('DD/MM/YYYY')}
                                    </div>
                                    <Button 
                                        icon={<CalendarOutlined />} 
                                        onClick={() => handleStatusChange(6)}
                                        size="small"
                                    >
                                        Editar Fechas de Prácticas
                                    </Button>
                                </div>
                            </>
                        )}
                        <Divider />
                        <Button
                            danger
                            icon={<StopOutlined />}
                            onClick={() => handleStatusChange(-1)}
                            type={selectedAssignacio.estat === -1 ? 'primary' : 'default'}
                            style={{ width: '100%' }}
                        >
                            {selectedAssignacio.estat === -1 ? 'Asignación Cancelada' : 'Cancelar Asignación'}
                        </Button>
                    </Card>


                        <h3>Documentos</h3>
                        <div style={{ marginBottom: 16 }}>
                            <Upload customRequest={handleUpload} showUploadList={false}>
                                <Button icon={<UploadOutlined />}>Subir Documento</Button>
                            </Upload>
                        </div>
                        <List
                            bordered
                            dataSource={documents}
                            renderItem={item => (
                                <List.Item
                                    actions={[
                                        <Button 
                                            key="download"
                                            icon={<DownloadOutlined />} 
                                            type="primary" 
                                            onClick={() => handleDownloadDoc(item.id, item.nom)}
                                        >
                                            Descarregar
                                        </Button>
                                    ]}
                                >
                                    <List.Item.Meta
                                        avatar={<FileTextOutlined />}
                                        title={item.nom}
                                        description={new Date(item.data_pujada).toLocaleString()}
                                    />
                                </List.Item>
                            )}
                        />
                    </div>
                )}
            </Modal>

            {/* DATES MODAL */}
            <Modal
                title="Fechas de Asignación (En curso)"
                open={isDatesModalVisible}
                onOk={handleDatesSubmit}
                onCancel={() => setIsDatesModalVisible(false)}
            >
                <Form form={datesForm} layout="vertical">
                    <p>Para pasar a estado "En curso", por favor introduzca las fechas reales.</p>
                    <Form.Item name="data_inici" label="Fecha Inicio" rules={[{ required: true }]}>
                        <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" placeholder='dd / mm / yyyy' />
                    </Form.Item>
                    <Form.Item name="data_fi" label="Fecha Fin" rules={[{ required: true }]}>
                        <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" placeholder='dd / mm / yyyy' />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default Asignaciones;
